import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import "./ChoosePlanStyle.css";

import PaymentsDetails from "../UserPlan/PaymentsDetails";
import FreePlanConfirmModal from "./FreePlanConfirmModal";
import { New_ScrollX2025 } from "../../assets/icons";

import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentPlan,
  getRendersPlan,
  getUserAllInformation,
  updateUserCardInfo,
} from "../../Redux/actions/commonActtion";

import { toast } from "react-toastify";
import CustomToast, {
  defaultcustomToatStyle,
} from "../../Components/CustomToast/CustomToast";
import { Loader2 } from "../../Components";

const FAQ_DATA = [
  {
    q: "How many projects can I create on each plan?",
    a: "Sprint and Independent both include one active title at a time. Studio includes unlimited active titles, ideal for post houses juggling multiple productions at once.",
  },
  {
    q: "Can I switch plans later?",
    a: "Yes. You can upgrade or downgrade at any time from your account settings. Changes take effect on your next billing cycle.",
  },
  {
    q: "What happens to my project if I cancel?",
    a: "Your project stays saved. If you resubscribe later, everything — your credits, templates, and settings — will be exactly where you left them.",
  },
  {
    q: "What's the difference between Sprint and Independent?",
    a: "Sprint is billed weekly and built for a single fast turnaround. Independent is billed monthly and designed to stay active through a title's full life cycle — festival rounds, re-deliveries, and last-minute changes included.",
  },
  {
    q: "Is there a limit on renders or exports?",
    a: "No. Every plan includes unlimited renders and exports at 4K and all major frame rates.",
  },
];

const AllPlansList = ({ setRenderOpen, isPlanShow }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("user"));

  const [isOpen, setIsOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState({});
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isFreePlanModalOpen, setIsFreePlanModalOpen] = useState(false);
  const [pendingFreePlan, setPendingFreePlan] = useState(null);

  const [selectedPlanForCheckout, setSelectedPlanForCheckout] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(-1);
  const faqRefs = useRef([]);
  const pageRef = useRef(null);
  const pageContentRef = useRef(null);

  const { renderPlan } = useSelector((state) => state.commonReducer);
  const commonReducer = useSelector((state) => state?.commonReducer);
  const currentPlan = commonReducer?.currentPlan;
  const isExpired = commonReducer?.currentPlan?.isExpired;
  const currentPlanDetails = currentPlan?.plan?.plan_detail;
  const [isUserPaid, setIsUserPaid] = useState(
    currentPlan.success && currentPlan.plan ? true : false,
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsDataLoading(true);
      try {
        await dispatch(getCurrentPlan());
        await dispatch(getUserAllInformation());
        await getRenderPlanDetails();
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    getRenderPlanDetails();
  }, [isSuccessOpen]);

  // Auto-select the first paid plan when renderPlan loads
  useEffect(() => {
    if (renderPlan && renderPlan.length > 0 && !selectedPlanForCheckout) {
      const paidPlans = renderPlan.filter((p) => p.price > 0);
      if (paidPlans.length > 0) {
        const midIndex = Math.min(1, paidPlans.length - 1);
        setSelectedPlanForCheckout(paidPlans[midIndex]);
      }
    }
  }, [renderPlan]);

  const getRenderPlanDetails = async () => {
    await dispatch(getRendersPlan(user));
    await dispatch(getCurrentPlan());
  };

  // Navigate to embedded checkout page
  const handlePlanUpgrade = async (plan) => {
    if (plan.price === 0) {
      toast(
        <CustomToast title="" content="You're already on the free plan" />,
        defaultcustomToatStyle,
      );
      return;
    }

    navigate("/checkout", { state: { plan } });
  };

  const handlePlanClick = async (plan) => {
    if (plan.price === 0) {
      setPendingFreePlan(plan);
      setIsFreePlanModalOpen(true);
      return;
    }

    const hasActiveSubscription =
      currentPlan?.plan?.subscription_status === 1 && !isExpired;

    if (hasActiveSubscription) {
      handleUpgradeOnSubscriptionActive(plan);
      return;
    }

    await handlePlanUpgrade(plan);
  };

  // Soft, lerp-based scroll (same feel as marketing upgrade pages)
  useEffect(() => {
    if (isDataLoading || isPlanShow) return;

    const wrapper = pageRef.current;
    const content = pageContentRef.current;
    if (!wrapper || !content) return;

    const lenis = new Lenis({
      wrapper,
      content,
      lerp: 0.075,
      duration: 1.15,
      smoothWheel: true,
      wheelMultiplier: 0.8,
      touchMultiplier: 1.15,
      syncTouch: true,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });

    let rafId = 0;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [isDataLoading, isPlanShow]);

  const createdAt = new Date(currentPlan?.plan?.createdAt);
  const planPurchaseDate = isNaN(createdAt.valueOf())
    ? ""
    : `${createdAt?.getDate()}/${
        createdAt?.getMonth() + 1
      }/${createdAt?.getFullYear()}`;

  const validityValue = parseInt(currentPlanDetails?.validity, 10) || 0;
  const [remainingDays, setRemainingDays] = useState("");
  const [remainingHours, setRemainingHours] = useState(0);
  const [remainingMinutes, setRemainingMinutes] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const today = new Date();
  const planRenewDate = new Date(
    createdAt?.getTime() + validityValue * 24 * 60 * 60 * 1000,
  );
  let timeDifference = planRenewDate - today;

  useEffect(() => {
    let prevRemainingDays = remainingDays;

    if (remainingDays === 1 || validityValue === 1) {
      const interval = setInterval(() => getTime(prevRemainingDays), 1000);
      return () => clearInterval(interval);
    } else {
      getTime(prevRemainingDays);
    }
  }, [remainingDays, validityValue, timeDifference]);

  const getTime = async (prevRemainingDays) => {
    if (!isNaN(validityValue)) {
      if (timeDifference > 0) {
        if (timeDifference >= 24 * 60 * 60 * 1000) {
          const updatedRemainingDays = Math.ceil(
            timeDifference / (24 * 60 * 60 * 1000),
          );
          timeDifference -= updatedRemainingDays * 24 * 60 * 60 * 1000;

          if (prevRemainingDays === 1 && updatedRemainingDays === 0) {
            setRemainingHours(0);
            setRemainingMinutes(0);
            setRemainingSeconds(0);
            setIsUserPaid(false);
          }
          setRemainingDays(updatedRemainingDays);
        }
        if (timeDifference >= 60 * 60 * 1000) {
          setRemainingHours(Math.floor(timeDifference / (60 * 60 * 1000)));
          timeDifference -= remainingHours * 60 * 60 * 1000;
        }

        if (timeDifference >= 60 * 1000) {
          setRemainingMinutes(Math.floor(timeDifference / (60 * 1000)));
          timeDifference -= remainingMinutes * 60 * 1000;
        }

        setRemainingSeconds(Math.floor(timeDifference / 1000));
      } else if (remainingDays === 0) {
        setIsUserPaid(false);
      } else {
        setRemainingDays(0);
        setRemainingHours(0);
        setRemainingMinutes(0);
        setRemainingSeconds(0);
      }
    }
  };

  const expirationMessage =
    remainingDays === 1 || validityValue === 1
      ? `Plan Expires in ${remainingHours}:${remainingMinutes}:${remainingSeconds}`
      : remainingDays > 1
        ? `Remaining days ${remainingDays}`
        : Object.keys(currentPlan).length === 0
          ? "Buy New Plan"
          : "Plan Expired ";

  const handleUpgradeOnSubscriptionActive = (plan) => {
    if (
      plan?._id !== currentPlan?.plan?.plan_detail?._id &&
      plan?.price !== 0
    ) {
      toast(
        <CustomToast
          title=""
          content={
            "You're already subscribed, Please cancel your current plan first and then upgrade your plan"
          }
        />,
        defaultcustomToatStyle,
      );
    }
  };

  const handleFreePlanConfirm = async () => {
    try {
      await dispatch(updateUserCardInfo());
      setIsFreePlanModalOpen(false);
      setPendingFreePlan(null);
      navigate("/dashboard");
    } catch (error) {
      toast(
        <CustomToast
          title="Error"
          content="Something went wrong. Please try again."
        />,
        defaultcustomToatStyle,
      );
    }
  };

  const handleFreePlanCancel = () => {
    setIsFreePlanModalOpen(false);
    setPendingFreePlan(null);
  };

  const handleSelectPlanCard = (plan) => {
    if (plan.price === 0) return;
    if (currentPlanDetails?._id === plan._id && !isExpired) return;
    setSelectedPlanForCheckout(plan);
  };

  const handleContinue = () => {
    if (!selectedPlanForCheckout) return;
    handlePlanClick(selectedPlanForCheckout);
  };

  const toggleFaq = (index) => {
    if (openFaqIndex === index) {
      setOpenFaqIndex(-1);
    } else {
      setOpenFaqIndex(index);
    }
  };

  const getPlanFeatures = (plan) => {
    return [plan.text1, plan.text2, plan.text3, plan.text4, plan.text6].filter(
      Boolean,
    );
  };

  const isCurrentActivePlan = (plan) => {
    return currentPlanDetails?._id === plan._id && !isExpired;
  };

  const paidPlans = renderPlan ? renderPlan.filter((p) => p.price > 0) : [];

  if (isDataLoading) {
    return (
      <div className="cp-loading">
        <div className="text-center">
          <Loader2 />
        </div>
      </div>
    );
  }

  return (
    <>
      {isPlanShow || (
        <div className="cp-page cp-page-upgrade" ref={pageRef}>
          <div className="cp-page-scroll-content" ref={pageContentRef}>
          <div className="cp-particles" />

          {/* ── Topbar ── */}
          <header className="cp-topbar">
            <div className="cp-logo">
              <a href="https://scrollx.io/" className="nav-logo">
                <img className="w-[35px]" src={New_ScrollX2025} alt="ScrollX" />
                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                  }}
                >
                  ScrollX
                </span>
              </a>
            </div>
            <div className="cp-topbar-right">
              <button
                type="button"
                onClick={() => navigate("/more_info", { state: { isDashboard: true } })}
                style={{
                  background: "none",
                  border: "none",
                  color: "#9c98bf",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  fontFamily: "Manrope, sans-serif",
                  padding: 0,
                  marginRight: "1rem",
                }}
              >
                ← Back to Billing
              </button>
            </div>
          </header>

          {/* ── Main Content ── */}
          <div className="cp-main">
            <div className="cp-page-header">
              <h1 className="cp-page-h1">Upgrade your plan.</h1>
              <p className="cp-page-sub">
                Get more features to accelerate your workflow.
              </p>
            </div>

            {/* ── Plan Cards ── */}
            <div
              className="cp-plans"
              style={{
                gridTemplateColumns:
                  paidPlans.length === 1
                    ? "minmax(0, calc((100% - 28px) / 3))"
                    : paidPlans.length === 2
                      ? "repeat(2, minmax(0, calc((100% - 28px) / 3)))"
                      : "repeat(3, 1fr)",
                justifyContent: "center",
              }}
            >
              {paidPlans.map((plan) => {
                const isSelected = selectedPlanForCheckout?._id === plan._id;
                const isCurrent = isCurrentActivePlan(plan);
                const isFeatured = Number(plan.price) === 89;

                return (
                  <div
                    className={`cp-plan${isSelected ? " cp-selected" : ""}${isFeatured ? " cp-featured" : ""}${isCurrent ? " cp-current-active" : ""}${isLoading ? " opacity-50 pointer-events-none" : ""}`}
                    key={plan._id}
                    onClick={() => handleSelectPlanCard(plan)}
                  >
                    <div className="cp-plan-check-ring">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      >
                        <path d="M5 12l5 5L20 7" />
                      </svg>
                    </div>

                    <span
                      className={`cp-plan-badge ${isFeatured ? "cp-badge-violet" : "cp-badge-dim"}`}
                    >
                      {isFeatured ? "Most Popular" : plan.tag || plan.type}
                    </span>

                    <div className="cp-plan-name">{plan.type}</div>

                    <div className="cp-plan-desc">
                      {plan.text5 || plan.teams || `${plan.type} plan`}
                    </div>

                    <div className="cp-plan-price-wrap">
                      <span className="cp-plan-price">
                        {plan.currency}
                        {plan.price}
                      </span>
                      <span className="cp-plan-period">
                        / {plan.interval_validity}
                      </span>
                    </div>
                    <div className="cp-plan-billing">
                      billed {plan.interval_validity}
                    </div>

                    <div className="cp-plan-hr" />

                    <ul className="cp-plan-features">
                      {getPlanFeatures(plan).map((feature, fi) => (
                        <li key={fi}>{feature}</li>
                      ))}
                    </ul>

                    {isCurrent && (
                      <div
                        style={{
                          marginTop: "16px",
                          padding: "6px 12px",
                          borderRadius: "8px",
                          background: "rgba(124,92,255,.15)",
                          border: "1px solid rgba(124,92,255,.3)",
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#c4b2ff",
                        }}
                      >
                        Current Plan
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Upgrade CTA ── */}
            <div className="cp-continue-wrap">
              <button
                className="cp-continue-btn"
                onClick={handleContinue}
                disabled={!selectedPlanForCheckout || isLoading}
              >
                Upgrade to {selectedPlanForCheckout?.type || "Plan"}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* ── FAQ ── */}
            <div className="cp-faq-section">
              <div className="cp-faq-section-header">
                <p className="cp-faq-section-eyebrow">Questions about plans</p>
                <div className="cp-faq-section-title">
                  Quick <em>answers.</em>
                </div>
              </div>
              <div className="cp-faq-list">
                {FAQ_DATA.map((faq, i) => (
                  <div
                    className={`cp-faq-item${openFaqIndex === i ? " cp-open" : ""}`}
                    key={i}
                  >
                    <button className="cp-faq-q" onClick={() => toggleFaq(i)}>
                      {faq.q}
                      <span className="cp-faq-q-plus">+</span>
                    </button>
                    <div
                      className="cp-faq-a"
                      ref={(el) => (faqRefs.current[i] = el)}
                      style={
                        openFaqIndex === i && faqRefs.current[i]
                          ? {
                              maxHeight:
                                faqRefs.current[i].scrollHeight + "px",
                            }
                          : { maxHeight: 0 }
                      }
                    >
                      {faq.a}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {isOpen && (
            <PaymentsDetails
              setRenderOpen={setRenderOpen}
              isPlanShow={isPlanShow}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              selectedPlan={selectedPlan}
              currentPlanDetails={currentPlanDetails}
              paymentType={currentPlan?.plan?.payment_type}
              order_id={currentPlan?.plan?.order_id}
              subscription_status={currentPlan?.plan?.subscription_status}
              planPurchaseDate={planPurchaseDate}
              getTime={getTime}
              remainingDays={remainingDays}
              expirationMessage={expirationMessage}
              setIsSuccessOpen={setIsSuccessOpen}
            />
          )}

          <FreePlanConfirmModal
            isOpen={isFreePlanModalOpen}
            onConfirm={handleFreePlanConfirm}
            onCancel={handleFreePlanCancel}
            planName={pendingFreePlan?.type || "Free"}
          />
          </div>
        </div>
      )}
    </>
  );
};

export default AllPlansList;
